/**
 * メールアプリ内ブラウザ（WebView / Chrome Custom Tabs）を検知するユーティリティ。
 *
 * Androidでメールからwebsurveyの回答URLを開いた際に、メールソフト内の
 * 組み込みブラウザ（WebView）やChrome Custom Tabs（CCT）で開かれてしまう場合がある。
 * このユーティリティでUserAgentとdocument.referrerを解析し、
 * メール内ブラウザかどうかを判別する。
 *
 * 検知方式:
 *  1. referrerベース: Android アプリが CCT で URL を開くと、Chrome が
 *     document.referrer を "android-app://[package-name]" に設定する。
 *  2. 非Chrome UA 検知: Gmail等の一部アプリはWebViewのUAから "Chrome/" を
 *     除外している。通常のChromeは必ず "Chrome/" を含むため、
 *     Android + WebKit ベースで "Chrome/" がなければアプリ内ブラウザと判定。
 *     実測値: Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 ... Safari/537.36
 *  3. UAベース: 従来の WebView ("; wv)" フラグ) やアプリ固有UA文字列で検知。
 */

/**
 * メールアプリ内ブラウザのUA検知パターン定義。
 * 各パターンには検知用の正規表現と、ユーザー向けの操作手順を含む。
 */
interface InAppBrowserPattern {
  /** UserAgentに対する正規表現 */
  regex: RegExp;
  /** メールアプリ名（ユーザー向け表示用） */
  appName: string;
  /** 外部ブラウザで開くための操作手順 */
  instruction: string;
  /**
   * WebViewフラグ("; wv)")チェックをスキップするかどうか。
   * Chrome Custom Tabs（CCT）等、WebViewフラグを持たないアプリ内ブラウザ向け。
   * true の場合、Android + アプリUA一致のみで検知する。
   */
  skipWebViewCheck?: boolean;
}

/**
 * Android アプリのパッケージ名とアプリ情報のマッピング。
 * document.referrer が "android-app://[package-name]" の形式で設定される
 * Chrome Custom Tabs（CCT）経由のアクセスを検知するために使用する。
 *
 * CCT の UA は通常の Chrome と完全に同一であり、UA からの検知は不可能。
 * そのため referrer ベースの検知が CCT 対応の主要手段となる。
 */
const ANDROID_APP_PACKAGES: Record<string, { appName: string; instruction: string }> = {
  // Gmail (2026-03 時点)
  // Gmail は独自WebView を使用し UA に "Chrome/" を含まない。referrer でも検知可能。
  // 実測UA: Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Safari/537.36
  'com.google.android.gm': {
    appName: 'Gmail',
    instruction: '右上の「⋮」メニューから「Chrome で開く」を選択してください。',
  },
  // Microsoft Outlook (2026-03 時点)
  'com.microsoft.office.outlook': {
    appName: 'Outlook',
    instruction: '右上の「⋮」メニューから「ブラウザで開く」を選択してください。',
  },
  // Yahoo!メール (日本版) (2026-03 時点)
  'jp.co.yahoo.android.ymail': {
    appName: 'Yahoo!メール',
    instruction: '右上のメニューから「ブラウザで開く」を選択してください。',
  },
  // Yahoo! Mail (グローバル版) (2026-03 時点)
  'com.yahoo.mobile.client.android.mail': {
    appName: 'Yahoo!メール',
    instruction: '右上のメニューから「ブラウザで開く」を選択してください。',
  },
  // Facebook (2026-03 時点)
  'com.facebook.katana': {
    appName: 'Facebook',
    instruction: '右下の「…」メニューから「ブラウザで開く」を選択してください。',
  },
  // LINE (2026-03 時点)
  'jp.naver.line.android': {
    appName: 'LINE',
    instruction: '右上のメニューから「他のブラウザで開く」を選択してください。',
  },
  // Instagram (2026-03 時点)
  'com.instagram.android': {
    appName: 'Instagram',
    instruction: '右上の「…」メニューから「ブラウザで開く」を選択してください。',
  },
  // Samsung Email (2026-03 時点)
  'com.samsung.android.email.provider': {
    appName: 'Samsung メール',
    instruction: '右上のメニューから「ブラウザで開く」を選択してください。',
  },
  // Google Search App (2026-03 時点)
  // GSA自身のWebViewで開く場合は UA に "GSA/" が含まれるが、
  // CCT経由の場合はreferrerで検知する
  'com.google.android.googlequicksearchbox': {
    appName: 'Google',
    instruction: '右上の「⋮」メニューから「Chrome で開く」を選択してください。',
  },
};

/**
 * UA ベースのアプリ内ブラウザパターン定義。
 * referrer ベース検知のフォールバックとして使用する。
 *
 * ※ 通常ブラウザ（Chrome, Firefox等）での誤判定を防ぐため、
 *   detectInAppBrowser() 内で「Android + WebView」を必須条件としたうえで
 *   これらパターンとの二重チェックを行う。
 *   ただし skipWebViewCheck: true のパターンは WebView チェックなしで検知する
 *   （アプリ固有UA文字列が通常ブラウザに含まれないことが前提）。
 */
const IN_APP_BROWSER_PATTERNS: InAppBrowserPattern[] = [
  {
    // Google Search App 内ブラウザ (2026-03 時点のUAパターン)
    // GSA が自身の WebView でリンクを開いた場合のみ UA に "GSA/" が含まれる。
    // Gmail は CCT を使うため "GSA/" を含まない → referrer ベース検知で対応。
    regex: /GSA\//i,
    appName: 'Google',
    instruction: '右上の「⋮」メニューから「Chrome で開く」を選択してください。',
    skipWebViewCheck: true,
  },
  {
    // Microsoft Outlook 内ブラウザ (2026-03 時点のUAパターン)
    // Outlook が WebView を使う場合は UA に "Outlook" が含まれる
    regex: /Outlook/i,
    appName: 'Outlook',
    instruction: '右上の「⋮」メニューから「ブラウザで開く」を選択してください。',
  },
  {
    // Yahoo!メール (2026-03 時点のUAパターン)
    regex: /YahooMobile/i,
    appName: 'Yahoo!メール',
    instruction: '右上のメニューから「ブラウザで開く」を選択してください。',
  },
  {
    // Facebook 内ブラウザ (2026-03 時点のUAパターン)
    regex: /FBAN|FBAV/i,
    appName: 'Facebook',
    instruction: '右下の「…」メニューから「ブラウザで開く」を選択してください。',
  },
  {
    // LINE 内ブラウザ (2026-03 時点のUAパターン)
    regex: /\bLine\//i,
    appName: 'LINE',
    instruction: '右上のメニューから「他のブラウザで開く」を選択してください。',
  },
  {
    // Instagram 内ブラウザ (2026-03 時点のUAパターン)
    regex: /Instagram/i,
    appName: 'Instagram',
    instruction: '右上の「…」メニューから「ブラウザで開く」を選択してください。',
  },
];

export interface InAppBrowserDetectionResult {
  /** メールアプリ内ブラウザであるか */
  isInAppBrowser: boolean;
  /** 検知されたメールアプリ名 */
  appName: string;
  /** 外部ブラウザで開くための操作手順 */
  instruction: string;
}

/**
 * 現在のブラウザがAndroidのメールアプリ内ブラウザかどうかを判定する。
 *
 * 判定ロジック（優先順位順）:
 *  1. referrerベース検知: document.referrer が "android-app://[package-name]" の場合、
 *     アプリ内ブラウザと判定。パッケージ名からアプリを特定する。
 *  2. 非Chrome UA 検知: Android + WebKit ベースで "Chrome/" を含まないUA。
 *     Gmail等のアプリ内ブラウザはUAから "Chrome/" を除外しているため検知可能。
 *     （通常のChrome/Firefox/Samsung Internet等は固有識別子を必ず含む）
 *  3. UAベース検知（skipWebViewCheck）: アプリ固有UA文字列のみで検知
 *     （GSA等、UA に固有文字列を含むアプリ向け）
 *  4. UAベース検知（WebView二重チェック）: WebViewフラグ("; wv)") + アプリ固有UA一致
 *  5. WebViewフラグのみ一致: 汎用メッセージで案内
 *
 * @returns 検知結果（アプリ名と操作手順を含む）
 */
export const detectInAppBrowser = (): InAppBrowserDetectionResult => {
  const ua = navigator.userAgent;

  // Android端末でない場合は対象外
  if (!/android/i.test(ua)) {
    return { isInAppBrowser: false, appName: '', instruction: '' };
  }

  // Step 1: referrerベース検知（Chrome Custom Tabs / Intent 対応）
  // Android アプリが CCT や Intent で URL を開くと、Chrome が document.referrer を
  // "android-app://[package-name]" 形式に設定する。
  // パッケージ名からアプリを特定し、アプリ固有の操作手順を表示できる。
  const referrer = document.referrer;
  if (referrer.startsWith('android-app://')) {
    const packageName = referrer.replace('android-app://', '').replace(/\/.*$/, '');
    const knownApp = ANDROID_APP_PACKAGES[packageName];
    if (knownApp) {
      return {
        isInAppBrowser: true,
        appName: knownApp.appName,
        instruction: knownApp.instruction,
      };
    }
    // 未知のアプリだが、android-app:// referrer があるためアプリ内ブラウザと判定
    return {
      isInAppBrowser: true,
      appName: 'アプリ',
      instruction: '右上のメニューから「ブラウザで開く」を選択してください。',
    };
  }

  // Step 2: 非Chrome UA 検知（Gmail等のストリップドWebView対応）
  // Gmail等の一部アプリは独自WebViewを使用し、UAから "Chrome/" を除外している。
  // 通常のChromeブラウザは必ず "Chrome/" を含むため、
  // Android + WebKit ベースで "Chrome/" がない場合はアプリ内ブラウザと判定。
  // 実測値: Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Safari/537.36
  //
  // 誤判定防止: Firefox ("Firefox/"), Opera ("OPR/"), Samsung Internet ("SamsungBrowser/"),
  // UC Browser ("UCBrowser/"), Edge ("Edg/") 等は固有識別子を含むため除外。
  const isWebKitBased = /AppleWebKit/i.test(ua);
  const hasChromeIdentifier = /Chrome\//i.test(ua);
  const hasOtherBrowserIdentifier = /Firefox\/|OPR\/|SamsungBrowser\/|UCBrowser\/|Edg\//i.test(ua);
  if (isWebKitBased && !hasChromeIdentifier && !hasOtherBrowserIdentifier) {
    return {
      isInAppBrowser: true,
      appName: 'メールアプリ',
      instruction: 'ブラウザアプリで開き直してください。URLをコピーしてChromeなどのブラウザに貼り付けてください。',
    };
  }

  // Step 3: UAベース検知 - WebViewチェック不要なアプリ（アプリ固有UA文字列で判別）
  for (const pattern of IN_APP_BROWSER_PATTERNS) {
    if (pattern.skipWebViewCheck && pattern.regex.test(ua)) {
      return {
        isInAppBrowser: true,
        appName: pattern.appName,
        instruction: pattern.instruction,
      };
    }
  }

  // Step 4: WebViewフラグ "; wv)" チェック（従来の WebView 向け）
  const isWebView = /; wv\)/.test(ua);
  if (!isWebView) {
    return { isInAppBrowser: false, appName: '', instruction: '' };
  }

  // Step 5: WebView確定後、既知アプリのパターンで特定を試みる
  for (const pattern of IN_APP_BROWSER_PATTERNS) {
    if (!pattern.skipWebViewCheck && pattern.regex.test(ua)) {
      return {
        isInAppBrowser: true,
        appName: pattern.appName,
        instruction: pattern.instruction,
      };
    }
  }

  // 既知アプリに該当しないが、WebViewであるため汎用メッセージで案内
  return {
    isInAppBrowser: true,
    appName: 'アプリ',
    instruction: '右上のメニューから「ブラウザで開く」を選択してください。',
  };
};
